import { z } from 'zod';
import { projetObligatoire } from './champs';

const adresseEmail = z.email();

/**
 * Schéma de la charge utile envoyée à la création d'un groupe d'e-mails.
 *
 * Chaque adresse est signalée sous son propre champ, par une clé email_<rang> :
 * flattenError n'indexe que le premier niveau, et une erreur posée sur
 * list_emails[2].email ressortirait sous list_emails sans dire laquelle.
 *
 * Les règles sont réunies dans un seul superRefine, sur un objet dont les
 * champs ne sont que typés : Zod n'exécute pas une règle d'objet quand un champ
 * a déjà échoué, et un nom manquant masquerait sinon les adresses invalides.
 * Le projet est converti en nombre une fois la validation passée.
 *
 * Un groupe sans adresse est accepté : le formulaire permet de le créer puis de
 * le compléter.
 */
const regles = (groupe, ctx) => {
  const signaler = (champ, message) =>
    ctx.addIssue({ code: 'custom', path: [champ], message });

  if (!projetObligatoire.safeParse(groupe.project_id).success) {
    signaler('project_id', 'selectionner_un_projet');
  }

  if (!groupe.email_group_name || groupe.email_group_name.trim() === '') {
    signaler('email_group_name', 'saisir_un_nom_de_groupe');
  }

  // Une ligne ajoutée puis laissée vide est aussi refusée : elle partait
  // jusqu'ici avec une adresse vide.
  (groupe.list_emails || []).forEach((destinataire, rang) => {
    const adresse = (destinataire?.email || '').trim();
    if (!adresseEmail.safeParse(adresse).success) {
      signaler(`email_${rang}`, 'saisir_une_adresse_email_valide');
    }
  });
};

const champs = {
  project_id: z.any(),
  email_group_name: z.string().nullish(),
  list_emails: z
    .array(
      z.looseObject({
        email: z.string().nullish(),
        list_alarms_levels_favorites: z.array(z.number()).nullish(),
      })
    )
    .nullish(),
};

const convertir = (groupe) => ({
  ...groupe,
  project_id: projetObligatoire.parse(groupe.project_id),
  email_group_name: groupe.email_group_name.trim(),
  list_emails: (groupe.list_emails || []).map((destinataire) => ({
    ...destinataire,
    email: destinataire.email.trim(),
  })),
});

export const schemaNouveauGroupeEmail = z
  .object(champs)
  .superRefine(regles)
  .transform(convertir);

/**
 * Schéma de la charge utile envoyée à la modification d'un groupe d'e-mails.
 * Même contrat, plus l'identifiant du groupe visé.
 */
export const schemaModificationGroupeEmail = z
  .object({
    ...champs,
    group_email_id: z.coerce.number({ message: 'valeur_invalide' }).int(),
  })
  .superRefine(regles)
  .transform(convertir);
